import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Pencil,
  Link2,
  Pause,
  Play,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import {
  activateCopySourceProfile,
  createCopySourceProfile,
  createCopyTradingLink,
  deleteCopySourceProfile,
  deleteCopyTradingLink,
  discoverCopySourceProfiles,
  fetchAccounts,
  getCopyTradingSummary,
  listCopyAuditLogs,
  listCopyMirrors,
  listCopySignals,
  listCopySourceProfiles,
  listCopyTradingLinks,
  pauseCopySourceProfile,
  pauseCopyTradingLink,
  resumeCopyTradingLink,
  updateCopySourceProfile,
  updateCopyTradingLink,
} from "../api/api";
import CustomSelect from "../components/ui/CustomSelect";

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.accounts)) return value.accounts;
  return [];
};

const mergeById = (...lists) => {
  const seen = new Set();
  const merged = [];
  lists.flat().filter(Boolean).forEach((item) => {
    const id = String(item.id ?? item.value ?? "");
    if (!id || seen.has(id)) return;
    seen.add(id);
    merged.push(item);
  });
  return merged;
};

const statusClass = (status) => {
  const value = String(status || "").toUpperCase();
  if (["ACTIVE", "COPIED", "COMPLETED"].includes(value)) {
    return "border-emerald-500/30 bg-emerald-500/10 text-emerald-300";
  }
  if (["PAUSED", "AWAITING_APPROVAL", "PROCESSING", "PARTIAL"].includes(value)) {
    return "border-amber-500/30 bg-amber-500/10 text-amber-300";
  }
  if (["FAILED", "DEGRADED", "STOPPED", "DISABLED"].includes(value)) {
    return "border-red-500/30 bg-red-500/10 text-red-300";
  }
  return "border-white/10 bg-white/[0.04] text-textSecondary";
};

const StatusPill = ({ status }) => (
  <span className={`inline-flex min-w-[5.5rem] justify-center rounded-full border px-2 py-1 text-[11px] font-semibold uppercase tracking-normal ${statusClass(status)}`}>
    {status || "unknown"}
  </span>
);

const Panel = ({ title, icon, action, children }) => (
  <section className="rounded-lg border border-borderDefault bg-backgroundSurface/70">
    <div className="flex items-center justify-between gap-3 border-b border-borderDefault px-4 py-3">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-sm font-semibold text-textPrimary">{title}</h2>
      </div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

const Field = ({ label, children }) => (
  <label className="flex min-w-0 flex-col gap-1 text-xs font-medium text-textSecondary">
    <span>{label}</span>
    {children}
  </label>
);

const CheckboxControl = ({ label, checked, onChange }) => (
  <label className="inline-flex h-9 items-center gap-2 rounded-md border border-borderDefault bg-backgroundBody px-3 text-sm text-textSecondary">
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    <span>{label}</span>
  </label>
);

const inputClass = "h-10 rounded-md border border-borderDefault bg-backgroundBody px-3 text-sm text-textPrimary outline-none transition focus:border-primary";
const textareaClass = "min-h-[72px] rounded-md border border-borderDefault bg-backgroundBody px-3 py-2 text-sm text-textPrimary outline-none transition focus:border-primary";
const buttonClass = "inline-flex h-10 items-center justify-center gap-2 rounded-md border border-borderDefault px-3 text-sm font-medium text-textPrimary transition hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50";
const primaryButtonClass = "inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-3 text-sm font-semibold text-textAccent transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50";

function formatAccount(account) {
  if (!account) return "Unknown account";
  const simpleId = account.simple_id ? ` #${account.simple_id}` : "";
  return `${account.name || "Account"}${simpleId} · ${account.platform || "Broker"}`;
}

function formatSourceProfile(source) {
  if (!source) return "Unknown source";
  const accountName = source.account_name || source.account || "Account";
  return `${source.name || "Source profile"} - ${accountName}`;
}

const createInitialSourceForm = (account = "") => ({
  account,
  name: "",
  description: "",
  status: "ACTIVE",
  is_public: false,
  copy_market_orders: true,
  copy_pending_orders: false,
  copy_sl_tp_updates: true,
  copy_partial_closes: true,
  allowed_symbols: "",
  blocked_symbols: "",
  min_follower_balance: "",
  max_followers: "",
});

const createInitialLinkForm = (followerAccount = "") => ({
  source_profile: "",
  follower_account: followerAccount,
  sizing_mode: "LOT_MULTIPLIER",
  fixed_lot: "",
  lot_multiplier: "1.0000",
  risk_percent: "1.00",
  max_lot_per_trade: "",
  max_open_copied_trades: "",
  max_symbol_exposure_lots: "",
  max_daily_loss: "",
  min_equity: "",
  max_slippage_points: "",
  require_approval: false,
  copy_market_orders: true,
  copy_pending_orders: false,
  copy_stop_loss: true,
  copy_take_profit: true,
  copy_sl_tp_updates: true,
  copy_partial_closes: true,
  allowed_symbols: "",
  blocked_symbols: "",
  symbol_map: "",
});

function CopyTradingPage() {
  const [accounts, setAccounts] = useState([]);
  const [sources, setSources] = useState([]);
  const [discoverableSources, setDiscoverableSources] = useState([]);
  const [links, setLinks] = useState([]);
  const [signals, setSignals] = useState([]);
  const [mirrors, setMirrors] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState({ sources: 0, followers: 0, approvals: 0, failed: 0 });
  const [tab, setTab] = useState("overview");
  const [sourceSearch, setSourceSearch] = useState("");
  const [sourcePage, setSourcePage] = useState(1);
  const [sourceHasMore, setSourceHasMore] = useState(false);
  const [sourceProfilesLoading, setSourceProfilesLoading] = useState(false);
  const [sourceProfilesLoadingMore, setSourceProfilesLoadingMore] = useState(false);
  const [selectedSourceSnapshot, setSelectedSourceSnapshot] = useState(null);
  const [editingSourceId, setEditingSourceId] = useState(null);
  const [editingLinkId, setEditingLinkId] = useState(null);

  const [sourceForm, setSourceForm] = useState(() => createInitialSourceForm());
  const [linkForm, setLinkForm] = useState(() => createInitialLinkForm());

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [
        accountsData,
        summaryData,
        sourcesData,
        linksData,
        signalsData,
        mirrorsData,
        auditData,
      ] = await Promise.all([
        fetchAccounts(),
        getCopyTradingSummary(),
        listCopySourceProfiles({ page_size: 200 }),
        listCopyTradingLinks({ page_size: 200 }),
        listCopySignals({ page_size: 200 }),
        listCopyMirrors({ page_size: 200 }),
        listCopyAuditLogs({ page_size: 200 }),
      ]);
      const accountList = asArray(accountsData);
      const ownSources = asArray(sourcesData);
      setAccounts(accountList);
      setSummary(summaryData || { sources: 0, followers: 0, approvals: 0, failed: 0 });
      setSources(ownSources);
      setLinks(asArray(linksData));
      setSignals(asArray(signalsData));
      setMirrors(asArray(mirrorsData));
      setAuditLogs(asArray(auditData));
      const usedSourceAccountIds = new Set(ownSources.map((source) => String(source.account)));
      const firstUnusedSourceAccount = accountList.find(
        (account) => !usedSourceAccountIds.has(String(account.id)),
      );
      setSourceForm((form) => {
        if (form.name) return form;
        const currentAccountIsAvailable = form.account
          && !usedSourceAccountIds.has(String(form.account));
        return {
          ...form,
          account: currentAccountIsAvailable
            ? form.account
            : firstUnusedSourceAccount?.id || "",
        };
      });
      setLinkForm((form) => ({
        ...form,
        follower_account: form.follower_account || accountList[0]?.id || "",
      }));
    } catch (err) {
      setError(err?.message || "Failed to load copy trading data.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDiscoverableSources = useCallback(async ({ search = "", page = 1, append = false } = {}) => {
    if (append) setSourceProfilesLoadingMore(true);
    else setSourceProfilesLoading(true);
    try {
      const data = await discoverCopySourceProfiles({ search, page, page_size: 50 });
      const items = asArray(data);
      setDiscoverableSources((previous) => (
        append ? mergeById(previous, items) : items
      ));
      setSourceHasMore(Boolean(data?.next));
      setSourcePage(page);
      setLinkForm((form) => ({
        ...form,
        source_profile: form.source_profile || items[0]?.id || "",
      }));
    } catch (err) {
      setError(err?.message || "Failed to load source profiles.");
    } finally {
      if (append) setSourceProfilesLoadingMore(false);
      else setSourceProfilesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      loadDiscoverableSources({ search: sourceSearch, page: 1, append: false });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [loadDiscoverableSources, sourceSearch]);

  const handleSourceSearchChange = useCallback((query) => {
    setSourceSearch(query);
  }, []);

  const loadMoreSourceProfiles = useCallback(() => {
    if (!sourceHasMore || sourceProfilesLoading || sourceProfilesLoadingMore) return;
    loadDiscoverableSources({
      search: sourceSearch,
      page: sourcePage + 1,
      append: true,
    });
  }, [
    loadDiscoverableSources,
    sourceHasMore,
    sourcePage,
    sourceProfilesLoading,
    sourceProfilesLoadingMore,
    sourceSearch,
  ]);

  const sourceChoices = useMemo(
    () => mergeById(selectedSourceSnapshot, discoverableSources, sources),
    [discoverableSources, selectedSourceSnapshot, sources],
  );

  const sourceById = useMemo(() => (
    Object.fromEntries(sourceChoices.map((source) => [source.id, source]))
  ), [sourceChoices]);

  const accountOptions = useMemo(() => accounts.map((account) => ({
    value: account.id,
    label: formatAccount(account),
    searchText: [
      account.name,
      account.simple_id,
      account.platform,
      account.broker_server,
      account.account_number,
    ].filter(Boolean).join(" "),
  })), [accounts]);

  const sourceAccountOptions = useMemo(() => {
    const usedAccountIds = new Set(
      sources
        .filter((source) => source.id !== editingSourceId)
        .map((source) => String(source.account)),
    );
    return accountOptions.filter((option) => !usedAccountIds.has(String(option.value)));
  }, [accountOptions, editingSourceId, sources]);

  const sourceProfileOptions = useMemo(() => sourceChoices.map((source) => ({
    value: source.id,
    label: source.name || "Source profile",
    searchText: [
      source.name,
      source.description,
      source.account_name,
      source.account,
      source.owner,
      ...(Array.isArray(source.allowed_symbols) ? source.allowed_symbols : []),
    ].filter(Boolean).join(" "),
    source,
  })), [sourceChoices]);

  const stats = useMemo(() => ({
    activeSources: Number(summary.sources || 0),
    activeLinks: Number(summary.followers || 0),
    awaiting: Number(summary.approvals || 0),
    failed: Number(summary.failed || 0),
  }), [summary]);

  const parseSymbols = (raw) => String(raw || "")
    .split(",")
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);

  const parseSymbolMap = (raw) => {
    const map = {};
    String(raw || "")
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((item) => {
        const [source, target] = item.split(/[:=]/).map((part) => part?.trim().toUpperCase());
        if (source && target) map[source] = target;
      });
    return map;
  };

  const handleSaveSource = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        account: sourceForm.account,
        name: sourceForm.name,
        description: sourceForm.description,
        status: sourceForm.status,
        is_public: sourceForm.is_public,
        allowed_symbols: parseSymbols(sourceForm.allowed_symbols),
        blocked_symbols: parseSymbols(sourceForm.blocked_symbols),
        copy_market_orders: sourceForm.copy_market_orders,
        copy_pending_orders: sourceForm.copy_pending_orders,
        copy_sl_tp_updates: sourceForm.copy_sl_tp_updates,
        copy_partial_closes: sourceForm.copy_partial_closes,
      };
      if (sourceForm.min_follower_balance) payload.min_follower_balance = sourceForm.min_follower_balance;
      if (sourceForm.max_followers) payload.max_followers = sourceForm.max_followers;
      if (editingSourceId) {
        await updateCopySourceProfile(editingSourceId, payload);
      } else {
        await createCopySourceProfile(payload);
      }
      setEditingSourceId(null);
      setSourceForm(createInitialSourceForm());
      await Promise.all([
        loadData(),
        loadDiscoverableSources({ search: sourceSearch, page: 1, append: false }),
      ]);
    } catch (err) {
      setError(err?.message || `Failed to ${editingSourceId ? "update" : "create"} source profile.`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLink = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        source_profile: linkForm.source_profile,
        follower_account: linkForm.follower_account,
        sizing_mode: linkForm.sizing_mode,
        lot_multiplier: linkForm.lot_multiplier || "1.0000",
        require_approval: linkForm.require_approval,
        allowed_symbols: parseSymbols(linkForm.allowed_symbols),
        blocked_symbols: parseSymbols(linkForm.blocked_symbols),
        copy_market_orders: linkForm.copy_market_orders,
        copy_pending_orders: linkForm.copy_pending_orders,
        copy_stop_loss: linkForm.copy_stop_loss,
        copy_take_profit: linkForm.copy_take_profit,
        copy_sl_tp_updates: linkForm.copy_sl_tp_updates,
        copy_partial_closes: linkForm.copy_partial_closes,
        symbol_map: parseSymbolMap(linkForm.symbol_map),
      };
      if (linkForm.sizing_mode === "FIXED_LOT") payload.fixed_lot = linkForm.fixed_lot;
      if (linkForm.sizing_mode === "RISK_PERCENT") payload.risk_percent = linkForm.risk_percent;
      if (linkForm.max_lot_per_trade) payload.max_lot_per_trade = linkForm.max_lot_per_trade;
      if (linkForm.max_open_copied_trades) payload.max_open_copied_trades = linkForm.max_open_copied_trades;
      if (linkForm.max_symbol_exposure_lots) payload.max_symbol_exposure_lots = linkForm.max_symbol_exposure_lots;
      if (linkForm.max_daily_loss) payload.max_daily_loss = linkForm.max_daily_loss;
      if (linkForm.min_equity) payload.min_equity = linkForm.min_equity;
      if (linkForm.max_slippage_points) payload.max_slippage_points = linkForm.max_slippage_points;
      if (editingLinkId) {
        await updateCopyTradingLink(editingLinkId, payload);
      } else {
        await createCopyTradingLink(payload);
      }
      setEditingLinkId(null);
      setSelectedSourceSnapshot(null);
      setLinkForm(createInitialLinkForm());
      await loadData();
    } catch (err) {
      setError(err?.message || `Failed to ${editingLinkId ? "update" : "create"} follower link.`);
    } finally {
      setSaving(false);
    }
  };

  const startEditSource = (source) => {
    setEditingSourceId(source.id);
    setSourceForm({
      account: source.account,
      name: source.name || "",
      description: source.description || "",
      status: source.status || "DRAFT",
      is_public: Boolean(source.is_public),
      copy_market_orders: Boolean(source.copy_market_orders),
      copy_pending_orders: Boolean(source.copy_pending_orders),
      copy_sl_tp_updates: Boolean(source.copy_sl_tp_updates),
      copy_partial_closes: Boolean(source.copy_partial_closes),
      allowed_symbols: (source.allowed_symbols || []).join(","),
      blocked_symbols: (source.blocked_symbols || []).join(","),
      min_follower_balance: source.min_follower_balance ?? "",
      max_followers: source.max_followers ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelSourceEdit = () => {
    const usedAccountIds = new Set(sources.map((source) => String(source.account)));
    const nextAccount = accounts.find(
      (account) => !usedAccountIds.has(String(account.id)),
    );
    setEditingSourceId(null);
    setSourceForm(createInitialSourceForm(nextAccount?.id || ""));
  };

  const removeSource = async (source) => {
    if (!window.confirm(`Remove source profile "${source.name}"? Existing copied positions will still receive lifecycle updates.`)) return;
    setSaving(true);
    setError("");
    try {
      await deleteCopySourceProfile(source.id);
      if (editingSourceId === source.id) cancelSourceEdit();
      await Promise.all([
        loadData(),
        loadDiscoverableSources({ search: sourceSearch, page: 1, append: false }),
      ]);
    } catch (err) {
      setError(err?.message || "Failed to remove source profile.");
    } finally {
      setSaving(false);
    }
  };

  const startEditLink = (link) => {
    const source = sourceById[link.source_profile] || {
      id: link.source_profile,
      name: link.source_profile_name,
      account_name: link.source_account_name,
    };
    setSelectedSourceSnapshot(source);
    setEditingLinkId(link.id);
    setLinkForm({
      source_profile: link.source_profile,
      follower_account: link.follower_account,
      sizing_mode: link.sizing_mode || "LOT_MULTIPLIER",
      fixed_lot: link.fixed_lot ?? "",
      lot_multiplier: link.lot_multiplier ?? "1.0000",
      risk_percent: link.risk_percent ?? "1.00",
      max_lot_per_trade: link.max_lot_per_trade ?? "",
      max_open_copied_trades: link.max_open_copied_trades ?? "",
      max_symbol_exposure_lots: link.max_symbol_exposure_lots ?? "",
      max_daily_loss: link.max_daily_loss ?? "",
      min_equity: link.min_equity ?? "",
      max_slippage_points: link.max_slippage_points ?? "",
      require_approval: Boolean(link.require_approval),
      copy_market_orders: Boolean(link.copy_market_orders),
      copy_pending_orders: Boolean(link.copy_pending_orders),
      copy_stop_loss: Boolean(link.copy_stop_loss),
      copy_take_profit: Boolean(link.copy_take_profit),
      copy_sl_tp_updates: Boolean(link.copy_sl_tp_updates),
      copy_partial_closes: Boolean(link.copy_partial_closes),
      allowed_symbols: (link.allowed_symbols || []).join(","),
      blocked_symbols: (link.blocked_symbols || []).join(","),
      symbol_map: Object.entries(link.symbol_map || {}).map(([from, to]) => `${from}:${to}`).join("\n"),
    });
  };

  const cancelLinkEdit = () => {
    setEditingLinkId(null);
    setSelectedSourceSnapshot(null);
    setLinkForm(createInitialLinkForm(accounts[0]?.id || ""));
  };

  const removeLink = async (link) => {
    if (!window.confirm("Remove this follower link? Existing copied positions will still receive lifecycle updates.")) return;
    setSaving(true);
    setError("");
    try {
      await deleteCopyTradingLink(link.id);
      if (editingLinkId === link.id) cancelLinkEdit();
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to remove follower link.");
    } finally {
      setSaving(false);
    }
  };

  const updateSourceStatus = async (source, next) => {
    setSaving(true);
    try {
      if (next === "ACTIVE") await activateCopySourceProfile(source.id);
      else await pauseCopySourceProfile(source.id);
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to update source profile.");
    } finally {
      setSaving(false);
    }
  };

  const updateLinkStatus = async (link, next) => {
    setSaving(true);
    try {
      if (next === "ACTIVE") await resumeCopyTradingLink(link.id);
      else await pauseCopyTradingLink(link.id);
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to update follower link.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-var(--topbar-height,0px))] bg-backgroundBody text-textPrimary">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-3 py-3 sm:px-4 lg:px-6">
        <div className="flex flex-col gap-3 border-b border-borderDefault pb-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-normal text-textPrimary">Copy Trading</h1>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-lg border border-borderDefault bg-backgroundSurface px-3 py-2">
                <p className="text-[11px] uppercase text-textSecondary">Sources</p>
                <p className="text-xl font-semibold">{stats.activeSources}</p>
              </div>
              <div className="rounded-lg border border-borderDefault bg-backgroundSurface px-3 py-2">
                <p className="text-[11px] uppercase text-textSecondary">Followers</p>
                <p className="text-xl font-semibold">{stats.activeLinks}</p>
              </div>
              <div className="rounded-lg border border-borderDefault bg-backgroundSurface px-3 py-2">
                <p className="text-[11px] uppercase text-textSecondary">Approvals</p>
                <p className="text-xl font-semibold">{stats.awaiting}</p>
              </div>
              <div className="rounded-lg border border-borderDefault bg-backgroundSurface px-3 py-2">
                <p className="text-[11px] uppercase text-textSecondary">Failed</p>
                <p className="text-xl font-semibold">{stats.failed}</p>
              </div>
            </div>
          </div>
          <button type="button" onClick={loadData} disabled={loading} className={buttonClass}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {error ? (
          <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {["overview", "sources", "followers", "mirrors", "signals"].map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`h-9 rounded-md px-3 text-sm font-medium capitalize transition ${tab === key ? "bg-primary text-textAccent" : "border border-borderDefault text-textSecondary hover:bg-primary/10"}`}
            >
              {key}
            </button>
          ))}
        </div>

        {(tab === "overview" || tab === "sources") && (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
            <Panel title={editingSourceId ? "Edit Source Profile" : "New Source Profile"} icon={<ShieldCheck className="h-4 w-4 text-primary" />}>
              <form onSubmit={handleSaveSource} className="grid gap-3">
                <Field label="Source account">
                  <CustomSelect
                    options={sourceAccountOptions}
                    value={sourceForm.account}
                    onChange={(value) => setSourceForm((form) => ({ ...form, account: value }))}
                    disabled={Boolean(editingSourceId)}
                    placeholder="Select source account"
                    searchable
                    searchPlaceholder="Search accounts..."
                    emptyText="No accounts found"
                    maxDropdownHeight={260}
                    buttonClassName="h-10 bg-backgroundBody"
                    dropdownFitContent={false}
                  />
                </Field>
                <Field label="Profile name">
                  <input className={inputClass} value={sourceForm.name} onChange={(event) => setSourceForm((form) => ({ ...form, name: event.target.value }))} required />
                </Field>
                <Field label="Description">
                  <textarea className={textareaClass} value={sourceForm.description} onChange={(event) => setSourceForm((form) => ({ ...form, description: event.target.value }))} placeholder="Describe this source strategy" />
                </Field>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Allowed symbols">
                    <input className={inputClass} value={sourceForm.allowed_symbols} onChange={(event) => setSourceForm((form) => ({ ...form, allowed_symbols: event.target.value }))} placeholder="EURUSD,XAUUSD" />
                  </Field>
                  <Field label="Blocked symbols">
                    <input className={inputClass} value={sourceForm.blocked_symbols} onChange={(event) => setSourceForm((form) => ({ ...form, blocked_symbols: event.target.value }))} />
                  </Field>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Min follower balance">
                    <input type="number" min="0" step="0.01" className={inputClass} value={sourceForm.min_follower_balance} onChange={(event) => setSourceForm((form) => ({ ...form, min_follower_balance: event.target.value }))} />
                  </Field>
                  <Field label="Max followers">
                    <input type="number" min="1" step="1" className={inputClass} value={sourceForm.max_followers} onChange={(event) => setSourceForm((form) => ({ ...form, max_followers: event.target.value }))} />
                  </Field>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <CheckboxControl label="Public" checked={sourceForm.is_public} onChange={(checked) => setSourceForm((form) => ({ ...form, is_public: checked }))} />
                  <CheckboxControl label="Market orders" checked={sourceForm.copy_market_orders} onChange={(checked) => setSourceForm((form) => ({ ...form, copy_market_orders: checked }))} />
                  <CheckboxControl label="Pending orders" checked={sourceForm.copy_pending_orders} onChange={(checked) => setSourceForm((form) => ({ ...form, copy_pending_orders: checked }))} />
                  <CheckboxControl label="SL/TP updates" checked={sourceForm.copy_sl_tp_updates} onChange={(checked) => setSourceForm((form) => ({ ...form, copy_sl_tp_updates: checked }))} />
                  <CheckboxControl label="Partial closes" checked={sourceForm.copy_partial_closes} onChange={(checked) => setSourceForm((form) => ({ ...form, copy_partial_closes: checked }))} />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <select className={`${inputClass} w-36`} value={sourceForm.status} onChange={(event) => setSourceForm((form) => ({ ...form, status: event.target.value }))}>
                    <option value="ACTIVE">Active</option>
                    <option value="DRAFT">Draft</option>
                    <option value="PAUSED">Paused</option>
                  </select>
                  <button type="submit" disabled={saving || !sourceForm.account || !sourceForm.name} className={primaryButtonClass}>
                    {editingSourceId ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    {editingSourceId ? "Save" : "Source"}
                  </button>
                  {editingSourceId ? (
                    <button type="button" onClick={cancelSourceEdit} disabled={saving} className={buttonClass}>
                      <X className="h-4 w-4" />
                      Cancel
                    </button>
                  ) : null}
                </div>
              </form>
            </Panel>

            <Panel title="Source Profiles" icon={<Activity className="h-4 w-4 text-primary" />}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="text-xs uppercase text-textSecondary">
                    <tr>
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Account</th>
                      <th className="px-3 py-2">Visibility</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borderDefault">
                    {sources.map((source) => (
                      <tr key={source.id}>
                        <td className="px-3 py-3 font-medium">{source.name}</td>
                        <td className="px-3 py-3 text-textSecondary">{source.account_name || source.account}</td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold uppercase ${source.is_public ? "border-sky-500/30 bg-sky-500/10 text-sky-300" : "border-white/10 bg-white/[0.04] text-textSecondary"}`}>
                            {source.is_public ? "Public" : "Private"}
                          </span>
                        </td>
                        <td className="px-3 py-3"><StatusPill status={source.status} /></td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-2">
                            {source.status === "ACTIVE" ? (
                              <button type="button" className={buttonClass} onClick={() => updateSourceStatus(source, "PAUSED")} disabled={saving}>
                                <Pause className="h-4 w-4" />
                                Pause
                              </button>
                            ) : (
                              <button type="button" className={buttonClass} onClick={() => updateSourceStatus(source, "ACTIVE")} disabled={saving}>
                                <Play className="h-4 w-4" />
                                Activate
                              </button>
                            )}
                            <button type="button" className={buttonClass} onClick={() => startEditSource(source)} disabled={saving}>
                              <Pencil className="h-4 w-4" />
                              Edit
                            </button>
                            <button type="button" className={`${buttonClass} border-red-500/30 text-red-300 hover:bg-red-500/10`} onClick={() => removeSource(source)} disabled={saving}>
                              <Trash2 className="h-4 w-4" />
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!sources.length && (
                      <tr><td className="px-3 py-6 text-textSecondary" colSpan={5}>No source profiles.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        )}

        {(tab === "overview" || tab === "followers") && (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <Panel title={editingLinkId ? "Edit Follower Link" : "New Follower Link"} icon={<Link2 className="h-4 w-4 text-primary" />}>
              <form onSubmit={handleSaveLink} className="grid gap-3">
                <Field label="Source profile">
                  <CustomSelect
                    options={sourceProfileOptions}
                    value={linkForm.source_profile}
                    onChange={(value) => {
                      setLinkForm((form) => ({ ...form, source_profile: value }));
                      setSelectedSourceSnapshot(sourceById[value] || null);
                    }}
                    disabled={Boolean(editingLinkId)}
                    placeholder="Search source profiles"
                    searchable
                    searchPlaceholder="Search source profiles..."
                    onSearchQueryChange={handleSourceSearchChange}
                    onEndReached={loadMoreSourceProfiles}
                    hasMore={sourceHasMore}
                    isLoadingMore={sourceProfilesLoadingMore}
                    emptyText={sourceProfilesLoading ? "Loading source profiles..." : "No source profiles found"}
                    loadingMoreText="Loading more sources..."
                    loadMoreText="Scroll for more sources"
                    maxDropdownHeight={320}
                    itemHeight={52}
                    buttonClassName="h-10 bg-backgroundBody"
                    dropdownFitContent={false}
                    renderOption={(option) => (
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium text-textPrimary">{option.label}</span>
                        <span className="truncate text-xs text-textSecondary">{formatSourceProfile(option.source)}</span>
                      </span>
                    )}
                    renderSelected={(option) => formatSourceProfile(option.source)}
                  />
                </Field>
                <Field label="Follower account">
                  <CustomSelect
                    options={accountOptions}
                    value={linkForm.follower_account}
                    onChange={(value) => setLinkForm((form) => ({ ...form, follower_account: value }))}
                    disabled={Boolean(editingLinkId)}
                    placeholder="Select follower account"
                    searchable
                    searchPlaceholder="Search accounts..."
                    emptyText="No accounts found"
                    maxDropdownHeight={260}
                    buttonClassName="h-10 bg-backgroundBody"
                    dropdownFitContent={false}
                  />
                </Field>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Sizing mode">
                    <select className={inputClass} value={linkForm.sizing_mode} onChange={(event) => setLinkForm((form) => ({ ...form, sizing_mode: event.target.value }))}>
                      <option value="LOT_MULTIPLIER">Multiplier</option>
                      <option value="FIXED_LOT">Fixed lot</option>
                      <option value="EQUITY_RATIO">Equity ratio</option>
                      <option value="BALANCE_RATIO">Balance ratio</option>
                      <option value="RISK_PERCENT">Risk percent</option>
                    </select>
                  </Field>
                  <Field label="Fixed lot">
                    <input type="number" min="0.01" step="0.01" className={inputClass} value={linkForm.fixed_lot} onChange={(event) => setLinkForm((form) => ({ ...form, fixed_lot: event.target.value }))} disabled={linkForm.sizing_mode !== "FIXED_LOT"} required={linkForm.sizing_mode === "FIXED_LOT"} />
                  </Field>
                  <Field label="Multiplier">
                    <input type="number" min="0.0001" step="0.0001" className={inputClass} value={linkForm.lot_multiplier} onChange={(event) => setLinkForm((form) => ({ ...form, lot_multiplier: event.target.value }))} />
                  </Field>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Risk percent">
                    <input type="number" min="0.01" step="0.01" className={inputClass} value={linkForm.risk_percent} onChange={(event) => setLinkForm((form) => ({ ...form, risk_percent: event.target.value }))} required={linkForm.sizing_mode === "RISK_PERCENT"} />
                  </Field>
                  <Field label="Max lot">
                    <input type="number" min="0.01" step="0.01" className={inputClass} value={linkForm.max_lot_per_trade} onChange={(event) => setLinkForm((form) => ({ ...form, max_lot_per_trade: event.target.value }))} />
                  </Field>
                  <Field label="Max open">
                    <input type="number" min="1" step="1" className={inputClass} value={linkForm.max_open_copied_trades} onChange={(event) => setLinkForm((form) => ({ ...form, max_open_copied_trades: event.target.value }))} />
                  </Field>
                </div>
                <div className="grid gap-3 sm:grid-cols-4">
                  <Field label="Symbol exposure">
                    <input type="number" min="0.01" step="0.01" className={inputClass} value={linkForm.max_symbol_exposure_lots} onChange={(event) => setLinkForm((form) => ({ ...form, max_symbol_exposure_lots: event.target.value }))} />
                  </Field>
                  <Field label="Daily loss">
                    <input type="number" min="0.01" step="0.01" className={inputClass} value={linkForm.max_daily_loss} onChange={(event) => setLinkForm((form) => ({ ...form, max_daily_loss: event.target.value }))} />
                  </Field>
                  <Field label="Min equity">
                    <input type="number" min="0.01" step="0.01" className={inputClass} value={linkForm.min_equity} onChange={(event) => setLinkForm((form) => ({ ...form, min_equity: event.target.value }))} />
                  </Field>
                  <Field label="Max slippage">
                    <input type="number" min="0" step="1" className={inputClass} value={linkForm.max_slippage_points} onChange={(event) => setLinkForm((form) => ({ ...form, max_slippage_points: event.target.value }))} />
                  </Field>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Allowed symbols">
                    <input className={inputClass} value={linkForm.allowed_symbols} onChange={(event) => setLinkForm((form) => ({ ...form, allowed_symbols: event.target.value }))} />
                  </Field>
                  <Field label="Blocked symbols">
                    <input className={inputClass} value={linkForm.blocked_symbols} onChange={(event) => setLinkForm((form) => ({ ...form, blocked_symbols: event.target.value }))} />
                  </Field>
                </div>
                <Field label="Symbol map">
                  <textarea className={textareaClass} value={linkForm.symbol_map} onChange={(event) => setLinkForm((form) => ({ ...form, symbol_map: event.target.value }))} placeholder="XAUUSD:XAUUSD.m, US30=US30.cash" />
                </Field>
                <div className="flex flex-wrap items-center gap-2">
                  <CheckboxControl label="Require approval" checked={linkForm.require_approval} onChange={(checked) => setLinkForm((form) => ({ ...form, require_approval: checked }))} />
                  <CheckboxControl label="Market orders" checked={linkForm.copy_market_orders} onChange={(checked) => setLinkForm((form) => ({ ...form, copy_market_orders: checked }))} />
                  <CheckboxControl label="Pending orders" checked={linkForm.copy_pending_orders} onChange={(checked) => setLinkForm((form) => ({ ...form, copy_pending_orders: checked }))} />
                  <CheckboxControl label="Stop loss" checked={linkForm.copy_stop_loss} onChange={(checked) => setLinkForm((form) => ({ ...form, copy_stop_loss: checked }))} />
                  <CheckboxControl label="Take profit" checked={linkForm.copy_take_profit} onChange={(checked) => setLinkForm((form) => ({ ...form, copy_take_profit: checked }))} />
                  <CheckboxControl label="SL/TP updates" checked={linkForm.copy_sl_tp_updates} onChange={(checked) => setLinkForm((form) => ({ ...form, copy_sl_tp_updates: checked }))} />
                  <CheckboxControl label="Partial closes" checked={linkForm.copy_partial_closes} onChange={(checked) => setLinkForm((form) => ({ ...form, copy_partial_closes: checked }))} />
                </div>
                <p className="text-xs text-textSecondary">
                  Pausing or removing a link stops new copied opens. Close, modify, and cancel events remain eligible for positions already copied through this link.
                </p>
                <div className="flex justify-end gap-3">
                  <button type="submit" disabled={saving || !linkForm.source_profile || !linkForm.follower_account} className={primaryButtonClass}>
                    {editingLinkId ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    {editingLinkId ? "Save" : "Link"}
                  </button>
                  {editingLinkId ? (
                    <button type="button" onClick={cancelLinkEdit} disabled={saving} className={buttonClass}>
                      <X className="h-4 w-4" />
                      Cancel
                    </button>
                  ) : null}
                </div>
              </form>
            </Panel>

            <Panel title="Follower Links" icon={<ShieldCheck className="h-4 w-4 text-primary" />}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="text-xs uppercase text-textSecondary">
                    <tr>
                      <th className="px-3 py-2">Source</th>
                      <th className="px-3 py-2">Follower</th>
                      <th className="px-3 py-2">Sizing</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borderDefault">
                    {links.map((link) => (
                      <tr key={link.id}>
                        <td className="px-3 py-3 font-medium">{sourceById[link.source_profile]?.name || link.source_profile_name || link.source_profile}</td>
                        <td className="px-3 py-3 text-textSecondary">{link.follower_account_name || link.follower_account}</td>
                        <td className="px-3 py-3 text-textSecondary">{link.sizing_mode}</td>
                        <td className="px-3 py-3"><StatusPill status={link.status} /></td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-2">
                            {link.status === "ACTIVE" ? (
                              <button type="button" className={buttonClass} onClick={() => updateLinkStatus(link, "PAUSED")} disabled={saving}>
                                <Pause className="h-4 w-4" />
                                Pause
                              </button>
                            ) : (
                              <button type="button" className={buttonClass} onClick={() => updateLinkStatus(link, "ACTIVE")} disabled={saving}>
                                <Play className="h-4 w-4" />
                                Resume
                              </button>
                            )}
                            <button type="button" className={buttonClass} onClick={() => startEditLink(link)} disabled={saving}>
                              <Pencil className="h-4 w-4" />
                              Edit
                            </button>
                            <button type="button" className={`${buttonClass} border-red-500/30 text-red-300 hover:bg-red-500/10`} onClick={() => removeLink(link)} disabled={saving}>
                              <Trash2 className="h-4 w-4" />
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!links.length && (
                      <tr><td className="px-3 py-6 text-textSecondary" colSpan={5}>No follower links.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        )}

        {(tab === "overview" || tab === "mirrors") && (
          <Panel title="Copy Mirrors" icon={<Activity className="h-4 w-4 text-primary" />}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase text-textSecondary">
                  <tr>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2">Follower</th>
                    <th className="px-3 py-2">Volume</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-borderDefault">
                  {mirrors.slice(0, tab === "overview" ? 8 : mirrors.length).map((mirror) => (
                    <tr key={mirror.id}>
                      <td className="px-3 py-3">{mirror.source_symbol}</td>
                      <td className="px-3 py-3">{mirror.copied_symbol}</td>
                      <td className="px-3 py-3 text-textSecondary">{mirror.source_volume || "-"} -&gt; {mirror.copied_volume || "-"}</td>
                      <td className="px-3 py-3"><StatusPill status={mirror.status} /></td>
                      <td className="max-w-md truncate px-3 py-3 text-textSecondary">{mirror.last_error || "-"}</td>
                    </tr>
                  ))}
                  {!mirrors.length && (
                    <tr><td className="px-3 py-6 text-textSecondary" colSpan={5}>No copied trade mirrors.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        {tab === "signals" && (
          <div className="grid gap-4 xl:grid-cols-2">
            <Panel title="Recent Signals" icon={<Activity className="h-4 w-4 text-primary" />}>
              <div className="space-y-2">
                {signals.map((signal) => (
                  <div key={signal.id} className="flex items-center justify-between gap-3 rounded-lg border border-borderDefault px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{signal.signal_type} · {signal.symbol}</p>
                      <p className="truncate text-xs text-textSecondary">{signal.source_event_id}</p>
                    </div>
                    <StatusPill status={signal.status} />
                  </div>
                ))}
                {!signals.length && <p className="py-6 text-sm text-textSecondary">No source signals.</p>}
              </div>
            </Panel>
            <Panel title="Audit Log" icon={<ShieldCheck className="h-4 w-4 text-primary" />}>
              <div className="space-y-2">
                {auditLogs.slice(0, 20).map((log) => (
                  <div key={log.id} className="rounded-lg border border-borderDefault px-3 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium">{log.event_type}</p>
                      <StatusPill status={log.level} />
                    </div>
                    <p className="mt-1 text-xs text-textSecondary">{log.message || "-"}</p>
                  </div>
                ))}
                {!auditLogs.length && <p className="py-6 text-sm text-textSecondary">No audit events.</p>}
              </div>
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
}

export default CopyTradingPage;
